import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import {
  EMERGENCY_COMPONENT_SOURCE,
  isEmergencyComponent,
  monthValue,
  numFmt,
  PAYOUT_TYPES,
} from './IncentiveRuleModal.constants';
import {
  btn, headerBar, headerCloseBtn, headerIconBox, headerLeft, headerTitle,
  input, label, modal, overlay, rowBox, section,
} from './IncentiveRuleModal.styles';
import IncentiveRuleVersionTimeline from './IncentiveRuleVersionTimeline';

function InlineField({ title, children }) {
  return <div><label style={label}>{title}</label>{children}</div>;
}

export default function IncentiveRuleModal({ rule, companyCode, ticketTypes, year, month, onSave, onClose }) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  const isEdit = !!rule;
  const pm = rule?.payout_method || {};
  const [name, setName] = useState(rule?.name || '');
  const [description, setDescription] = useState(rule?.description || '');
  const [isActive, setIsActive] = useState(rule?.is_active !== false);
  const [effectiveMonth, setEffectiveMonth] = useState(monthValue(year, month));
  const [components, setComponents] = useState(rule?.formula_components?.length ? rule.formula_components : []);
  const [condType, setCondType] = useState(rule?.conditions?.type || 'none');
  const [condValue, setCondValue] = useState(rule?.conditions?.value || '');
  const [payType, setPayType] = useState(pm.type === 'emergency_team_weekly' ? 'per_point' : (pm.type || 'threshold_excess'));
  const [payThreshold, setPayThreshold] = useState(pm.threshold ?? '');
  const [payUnitPrice, setPayUnitPrice] = useState(pm.unit_price ?? '');
  const [payFixedAmount, setPayFixedAmount] = useState(pm.amount ?? '');
  const [allUsers, setAllUsers] = useState([]);
  const [assignedIds, setAssignedIds] = useState(new Set());
  const [origAssignedIds, setOrigAssignedIds] = useState(new Set());
  const [assignmentMap, setAssignmentMap] = useState({});
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const selectedUserIds = useMemo(() => [...assignedIds], [assignedIds]);
  const versionTimeline = rule?.version_timeline || [];
  const emergencyComp = components.find(isEmergencyComponent);

  const loadUsers = useCallback(async () => {
    const res = await apiClient.get(`/api/admin/incentive/assignable-users?company_code=${companyCode}`);
    if (res.ok) setAllUsers(res.data?.data || []);
  }, [companyCode]);

  const loadAssignments = useCallback(async () => {
    if (!rule?.id) return;
    const res = await apiClient.get(`/api/admin/incentive/assignments?rule_id=${rule.id}`);
    if (!res.ok) return;
    const rows = res.data?.data || [];
    const ids = new Set(rows.map((assignment) => Number(assignment.user_id)));
    const nextMap = {};
    rows.forEach((assignment) => { nextMap[Number(assignment.user_id)] = assignment.id; });
    setAssignedIds(ids);
    setOrigAssignedIds(new Set(ids));
    setAssignmentMap(nextMap);
  }, [rule?.id]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  function toggleProduct(ticketTypeId) {
    setComponents((prev) => {
      const exists = prev.some((component) => component.ticket_type_id === ticketTypeId);
      if (exists) return prev.filter((component) => component.ticket_type_id !== ticketTypeId);
      return [...prev, { ticket_type_id: ticketTypeId, weight: 1 }];
    });
  }

  function setWeight(ticketTypeId, weight) {
    setComponents((prev) => prev.map((component) => (
      component.ticket_type_id === ticketTypeId ? { ...component, weight: Number(weight) || 0 } : component
    )));
  }

  function toggleEmergencyTeam() {
    setComponents((prev) => {
      if (prev.some(isEmergencyComponent)) return prev.filter((component) => !isEmergencyComponent(component));
      return [...prev, { source: EMERGENCY_COMPONENT_SOURCE, label: '비상대응팀 주말 배정', weight: 1 }];
    });
  }

  function setEmergencyWeight(weight) {
    setComponents((prev) => prev.map((component) => (
      isEmergencyComponent(component) ? { ...component, weight: Number(weight) || 0 } : component
    )));
  }

  function toggleUser(userId) {
    const id = Number(userId);
    setAssignedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (allUsers.length > 0 && assignedIds.size === allUsers.length) setAssignedIds(new Set());
    else setAssignedIds(new Set(allUsers.map((user) => Number(user.id))));
  }

  function buildBody() {
    const payoutMethod = { type: payType };
    if (payType === 'threshold_excess') {
      payoutMethod.threshold = Number(payThreshold) || 0;
      payoutMethod.unit_price = Number(payUnitPrice) || 0;
    } else if (payType === 'per_point') {
      payoutMethod.unit_price = Number(payUnitPrice) || 0;
    } else if (payType === 'fixed') {
      payoutMethod.amount = Number(payFixedAmount) || 0;
    }
    return {
      company_code: companyCode,
      name,
      description,
      formula_components: components,
      conditions: condType === 'none' ? null : { type: condType, value: Number(condValue) || 0 },
      payout_method: payoutMethod,
      is_active: isActive,
      effective_month: effectiveMonth,
    };
  }

  async function syncAssignments(ruleId) {
    const toAdd = [...assignedIds].filter((id) => !origAssignedIds.has(id));
    if (toAdd.length) await apiClient.post('/api/admin/incentive/assignments', { rule_id: ruleId, user_ids: toAdd });
    for (const userId of [...origAssignedIds].filter((id) => !assignedIds.has(id))) {
      if (assignmentMap[userId]) await apiClient.del(`/api/admin/incentive/assignments/${assignmentMap[userId]}`);
    }
    setOrigAssignedIds(new Set(assignedIds));
  }

  async function handlePreview() {
    if (!name.trim()) { alert('규칙 이름을 먼저 입력해 주세요.'); return; }
    if (!components.length) { alert('계산 항목을 하나 이상 선택해 주세요.'); return; }
    const res = await apiClient.post('/api/admin/incentive/preview', {
      company_code: companyCode,
      draft_rule: buildBody(),
      user_ids: selectedUserIds,
      year,
      month,
    });
    if (res.ok) setPreview(res.data?.data);
    else alert(res.data?.message || '미리보기 실패');
  }

  async function handleSave() {
    if (!name.trim()) { alert('규칙 이름을 입력해 주세요.'); return; }
    if (!components.length) { alert('계산 항목을 하나 이상 선택해 주세요.'); return; }
    setSaving(true);
    try {
      let ruleId = rule?.id;
      const res = isEdit
        ? await apiClient.put(`/api/admin/incentive/rules/${rule.id}`, buildBody())
        : await apiClient.post('/api/admin/incentive/rules', buildBody());
      if (!res.ok) throw new Error(res.data?.message || (isEdit ? '수정 실패' : '생성 실패'));
      if (!isEdit) ruleId = res.data?.data?.id;
      await syncAssignments(ruleId);
      onSave();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={overlay} {...overlayCloseProps}>
      <div style={modal} onClick={(event) => event.stopPropagation()}>
        <div style={headerBar}>
          <div style={headerLeft}>
            <div style={headerIconBox}>💰</div>
            <div style={headerTitle}>{isEdit ? '인센티브 규칙 수정' : '인센티브 규칙 추가'}</div>
          </div>
          <button style={headerCloseBtn} onClick={onClose}>✕</button>
        </div>

        <div style={{ ...section, ...rowBox, background: 'rgba(124,58,237,0.06)' }}>
          <label style={label}>적용 시작월</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="month" style={{ ...input, width: 160 }} value={effectiveMonth} onChange={(event) => setEffectiveMonth(event.target.value)} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              이 월부터 새 기준을 적용합니다. 이전 월 계산 기준은 유지됩니다.
            </span>
          </div>
        </div>

        <IncentiveRuleVersionTimeline versions={versionTimeline} />

        <div style={section}>
          <label style={label}>규칙 이름 *</label>
          <input style={input} value={name} onChange={(event) => setName(event.target.value)} placeholder="예: TM 파트 인센티브" />
        </div>
        <div style={section}>
          <label style={label}>설명</label>
          <input style={input} value={description} onChange={(event) => setDescription(event.target.value)} />
        </div>
        <div style={section}>
          <label style={label}>활성 상태</label>
          <select style={input} value={isActive ? 'true' : 'false'} onChange={(event) => setIsActive(event.target.value === 'true')}>
            <option value="true">활성</option>
            <option value="false">비활성</option>
          </select>
        </div>

        <div style={section}>
          <label style={label}>계산 항목별 가중치</label>
          <div style={{ ...rowBox, maxHeight: 180, overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0 8px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
              <input type="checkbox" checked={!!emergencyComp} onChange={toggleEmergencyTeam} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 800 }}>비상대응팀 주말 배정</span>
              {emergencyComp && <input type="number" step="0.1" min="0" value={emergencyComp.weight} onChange={(event) => setEmergencyWeight(event.target.value)} style={{ ...input, width: 72, textAlign: 'right', padding: '3px 6px' }} />}
              {emergencyComp && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>배</span>}
            </div>
            {ticketTypes.map((ticketType) => {
              const component = components.find((item) => item.ticket_type_id === ticketType.id);
              return (
                <div key={ticketType.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <input type="checkbox" checked={!!component} onChange={() => toggleProduct(ticketType.id)} />
                  <span style={{ flex: 1, fontSize: 13 }}>{ticketType.name}</span>
                  {component && <input type="number" step="0.1" min="0" value={component.weight} onChange={(event) => setWeight(ticketType.id, event.target.value)} style={{ ...input, width: 72, textAlign: 'right', padding: '3px 6px' }} />}
                  {component && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>배</span>}
                </div>
              );
            })}
            {!ticketTypes.length && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>상품 데이터 없음</div>}
          </div>
        </div>

        <div style={section}>
          <label style={label}>지급 조건</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select style={{ ...input, width: 'auto' }} value={condType} onChange={(event) => setCondType(event.target.value)}>
              <option value="none">조건 없음</option>
              <option value="contract_rate_min">최소 계약률(%)</option>
            </select>
            {condType === 'contract_rate_min' && (
              <>
                <input type="number" style={{ ...input, width: 90 }} value={condValue} onChange={(event) => setCondValue(event.target.value)} placeholder="10" />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>% 이상일 때만 지급</span>
              </>
            )}
          </div>
        </div>

        <div style={section}>
          <label style={label}>지급 방식</label>
          {PAYOUT_TYPES.map((type) => (
            <label key={type.value} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" name="payType" checked={payType === type.value} onChange={() => setPayType(type.value)} />
              {type.label}
            </label>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {payType === 'threshold_excess' && (
              <>
                <InlineField title="기준 점수"><input type="number" style={{ ...input, width: 120 }} value={payThreshold} onChange={(event) => setPayThreshold(event.target.value)} /></InlineField>
                <InlineField title="단가(원)"><input type="number" style={{ ...input, width: 120 }} value={payUnitPrice} onChange={(event) => setPayUnitPrice(event.target.value)} /></InlineField>
              </>
            )}
            {payType === 'per_point' && (
              <InlineField title="단가(원)"><input type="number" style={{ ...input, width: 120 }} value={payUnitPrice} onChange={(event) => setPayUnitPrice(event.target.value)} /></InlineField>
            )}
            {payType === 'fixed' && (
              <InlineField title="고정 금액(원)"><input type="number" style={{ ...input, width: 160 }} value={payFixedAmount} onChange={(event) => setPayFixedAmount(event.target.value)} /></InlineField>
            )}
          </div>
        </div>

        <div style={section}>
          <label style={label}>직원 배정 ({assignedIds.size}명 선택)</label>
          <div style={{ ...rowBox, maxHeight: 180, overflowY: 'auto' }}>
            <label style={{ display: 'flex', gap: 6, marginBottom: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={allUsers.length > 0 && assignedIds.size === allUsers.length} onChange={toggleAll} /> 전체 선택
            </label>
            {allUsers.map((user) => {
              const userId = Number(user.id);
              const checked = assignedIds.has(userId);
              const wasOriginal = origAssignedIds.has(userId);
              const status = checked && wasOriginal ? '배정중' : checked && !wasOriginal ? '추가 예정' : !checked && wasOriginal ? '해제 예정' : '';
              return (
                <label key={user.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', padding: '3px 6px', marginBottom: 2 }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleUser(user.id)} />
                  {user.name} <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>({user.department || user.worker_type})</span>
                  {status && <span style={{ fontSize: 10, fontWeight: 800, color: '#6366f1', background: 'rgba(99,102,241,.18)', padding: '1px 5px', borderRadius: 4 }}>{status}</span>}
                </label>
              );
            })}
            {!allUsers.length && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>대상 직원 없음</div>}
          </div>
        </div>

        {assignedIds.size > 0 && (
          <div style={section}>
            <button style={btn('#0891b2')} onClick={handlePreview}>미리 계산해보기 ({year}년 {month}월)</button>
            {preview && (
              <div style={{ marginTop: 8, ...rowBox, fontSize: 12, maxHeight: 200, overflowY: 'auto' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{preview.rule_name} / {preview.year}년 {preview.month}월 미리보기</div>
                {preview.results?.map((result) => (
                  <div key={result.user_id} style={{ display: 'flex', gap: 12, padding: '3px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ flex: 1 }}>{result.user_name}</span>
                    <span>점수: {numFmt(result.raw_score)}</span>
                    <span>조건: {result.condition_met ? '충족' : '미달'}</span>
                    <span style={{ fontWeight: 700 }}>{numFmt(result.calculated_amount)}원</span>
                  </div>
                ))}
                {(!preview.results || !preview.results.length) && <div style={{ color: 'var(--text-muted)' }}>배정된 직원 없음</div>}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button style={btn('#999')} onClick={onClose}>취소</button>
          <button style={btn('#7c3aed')} onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : (isEdit ? `${effectiveMonth}부터 적용` : '생성')}
          </button>
        </div>
      </div>
    </div>
  );
}
